'use client';

import styled from '@emotion/styled';
import GenericPage, { IGenericPageProps } from '../../app/pages/ui/GenericPage';
import { ContentWrap, Css, H3, Responsive } from '../../../ui';
import { SECURITY_API } from '../domain/api';
import { useApi } from '../../../data/hooks/useApi';
import { ISecurityCheckpointResponse } from '../domain/models/ISecurityCheckpointResponse';
import { getCurrentAirportCodeFromId } from '../../../components/common/navigation/utils/getCurrentAirportCode';
import RefreshData from '../../../components/common/refresh-data/RefreshData';
import { filterCheckpoints } from '../domain/utils/filterCheckpoints';
import SecurityCardsShimmer from './SecurityCardsShimmer';
import ErrorMessage from '../../shared/components/error/ErrorMessage';
import { ErrorMessages } from '../../../utils/errorMessages';
import CheckpointsList from './CheckpointsList';
import { IPoiItem } from '../../shop-dine-relax/domain/models/IPoiItem';
import { ISiteSectionEntry } from '../../../contentful/types/ISiteSection';
import { ICheckpointEntry } from '../../../contentful/types/ICheckpointEntry';
import { applyContentfulOverrides } from '../domain/utils/applyContentfulOverrides';

interface ISecurityPagePageProps extends IGenericPageProps {
	securityPoiData?: IPoiItem[];
	siteSection?: ISiteSectionEntry;
	contentfulCheckpoints?: ICheckpointEntry[];
}

export default function SecurityPage({
	page,
	securityPoiData,
	siteSection,
	contentfulCheckpoints,
}: ISecurityPagePageProps) {
	const airportCode = getCurrentAirportCodeFromId(
		page.fields.parent?.fields.id,
	);

	const { data, loading, error, refetch, lastUpdatedAt } =
		useApi<ISecurityCheckpointResponse>().useGetApi({
			url: `${SECURITY_API}/${airportCode}`,
			apiVersion: '120',
			pollingIntervalMs: 60000,
		});

	const checkpoints = data?.data.wait_times;

	// FIS should not be included in this list
	const filteredCheckpoints = checkpoints && filterCheckpoints(checkpoints);

	const mergedCheckpoints =
		filteredCheckpoints &&
		applyContentfulOverrides(filteredCheckpoints, contentfulCheckpoints);

	const renderErrorMessage = () => {
		if (error || !data) {
			return <ErrorMessage message={ErrorMessages.GenericError} />;
		}

		if (mergedCheckpoints?.length === 0) {
			return (
				<ErrorMessage message={ErrorMessages.SecurityCheckpointsNotFound} />
			);
		}
		return null;
	};

	return (
		<GenericPage page={page} siteSection={siteSection}>
			<InfoCardPageContentWrap>
				<InfoCardSectionTitle styling="h4">
					Security Checkpoints
				</InfoCardSectionTitle>

				{loading && !error ? (
					<SecurityCardsShimmer />
				) : (
					renderErrorMessage() || (
						<CheckpointsList
							checkpoints={mergedCheckpoints}
							securityPoiData={securityPoiData}
						/>
					)
				)}

				{mergedCheckpoints?.length !== 0 && (
					<RefreshData
						label="Refresh Wait Times"
						refetch={refetch}
						lastUpdatedAt={lastUpdatedAt}
					/>
				)}
			</InfoCardPageContentWrap>
		</GenericPage>
	);
}

export const InfoCardPageContentWrap = styled(ContentWrap)`
	margin-top: ${Css.pxToRem(80)} !important;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: ${Css.pxToRem(32)} !important;
	}
`;

export const InfoCardSectionTitle = styled(H3)`
	margin-bottom: ${Css.pxToRem(24)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(24)};
	}
`;
